import React, { ChangeEvent, Component, ReactNode } from 'react';
import ListVariableCreatorComponent from './ListVariableCreatorComponent';
import { ColumnProps } from 'primereact/column';
import CustomInput from '../../custom/CustomInput';
import { customConfirm } from '../../custom/CustomPopupConfirm';
import CustomTable from '../../custom/CustomTable';
import { ListColumnField } from '../../../domain/list/ListDomains';
import CustomEmptyComponent from '../../custom/CustomEmptyComponent';
import MessageUtil from '../../../util/MessageUtil';
import { dispatch, RootState } from '../../../redux/store';
import { changeList, changeListData, changeListIsUpdate } from '../../../redux/actions/ListActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { ListState } from '../../../redux/reducers/ListReducer';
import DataTypeSelectBoxComponent from '../../custom/DataTypeSelectBoxComponent';
import DataType from '../../../constant/DataType';
import RoleHelper from '../../../RoleHelper';
import { FormicaSelectBoxItem } from '../../custom/FormicaSearchSelectBox';
import { ComponentState, setSelectedItems } from '../../../redux/actions/ComponentActions';
import { RadioButton } from 'primereact/radiobutton';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../../../constant/CollectionNames';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import { ComponentDependency } from '../../../domain/ui/ComponentMetadata';
import { ListType } from '../../../constant/Constants';
import { DataTableDataSelectableParams } from 'primereact/datatable';
import ComponentName from '../../../constant/ComponentName';
import Computation = subscribe.Computation;
import QueryBuilder = query.QueryBuilder;
import eq = query.eq;
import and = query.and;
import LanguageUtil from '../../../util/LanguageUtil';
import { openBulkActionDialogFromDependency } from '../../../redux/actions/BulkActionActions';

class ListVariableComponent extends Component<Props> {
  private componentDependencySubscription: subscribe.Subscription | undefined;
  private structSubscription: subscribe.Subscription | undefined;
  private computation: Computation | undefined;

  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  private excluded = ['item', 'description', 'expireAt', 'createdAt', 'lastUpdated'];

  public render(): ReactNode {
    const {
      list: { list },
      component: { selectedComponentIds },
    } = this.props;
    return (
      <div className={'flex w-full h-full flex-col py-5 gap-y-3 px-5 !max-h-[calc(100vh-21.25rem)] overflow-y-scroll '}>
        <ListVariableCreatorComponent />
        {list.fields.length == 0 ? (
          <CustomEmptyComponent
            label={LanguageUtil.get({
              id: 'common.label.startYourTransactionsByCreatingNewList',
              defaultMessage: 'You can start your transactions by creating a new list.',
            })}
          />
        ) : (
          <CustomTable
            columns={this.getColumns()}
            selection={selectedComponentIds}
            onSelectionChange={this.onSelectionChange}
            value={list.fields}
            selectionMode='checkbox'
            showSelectAll={list.fields.some((f) => !this.excluded.includes(f.name))}
            isDataSelectable={this.isRowSelectable}
          />
        )}
      </div>
    );
  }

  public componentDidMount() {
    dispatch(setSelectedItems([]));
    this.startTracker();
  }

  public componentWillUnmount() {
    this.stopTracker();
  }

  private startTracker = () => {
    const {
      list: { list },
    } = this.props;
    const dependencyQuery = QueryBuilder.select()
      .from(CollectionNames.COMPONENT_DEPENDENCY)
      .where(and(eq('dependentType', 'ListField'), eq('meta.list', list.name)))
      .build();
    this.componentDependencySubscription = subscribe.Formica.subscribe(CollectionNames.COMPONENT_DEPENDENCY, dependencyQuery);
    this.structSubscription = subscribe.Formica.subscribe(CollectionNames.DATA_STRUCTURES, QueryBuilder.select().from(CollectionNames.DATA_STRUCTURES).build());
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.componentDependencySubscription, this.structSubscription]);
  };

  private tracker = async () => {
    const {
      list: { list },
    } = this.props;
    if (this.componentDependencySubscription?.ready() && this.structSubscription?.ready()) {
      const dependencyQuery = QueryBuilder.select()
        .from(CollectionNames.COMPONENT_DEPENDENCY)
        .where(and(eq('dependentType', 'ListField'), eq('meta.list', list.name)))
        .build();
      const dependencies: ComponentDependency[] = await this.repository.findMany(dependencyQuery);
      this.setState({ dependencies });
    }
  };

  private stopTracker = async () => {
    await this.componentDependencySubscription?.stop();
    await this.structSubscription?.stop();
    this.computation?.stop();
  };

  private isListDeployed = () => {
    const {
      list: { list },
    } = this.props;
    return list.stage != 'DRAFT';
  };

  private onSelectionChange = (e: any) => {
    dispatch(setSelectedItems(e.value));
  };

  private removeVariable = (listColumnField: ListColumnField) => {
    const {
      list: { list, dependencies },
      changeList,
      type,
    } = this.props;
    if (this.isListDeployed()) {
      MessageUtil.warning({
        summary: LanguageUtil.get({ id: 'common.label.list', defaultMessage: 'List' }),
        content: LanguageUtil.get({ id: 'messages.warning.cannotRemoveWhenDeployed', defaultMessage: 'Can not remove field when list is deployed.' }),
      });
      return;
    }

    const isUsed: ComponentDependency[] = dependencies.filter((d) => d.dependentId == listColumnField.name && d.meta.list == list.name);
    if (isUsed.length > 0) {
      dispatch(openBulkActionDialogFromDependency({ id: listColumnField.name, status: false, dependencies: isUsed, reason: 'DELETE' }, ComponentName.LIST));
      return;
    }

    if (
      type == 'INTERNAL_LIST' &&
      (listColumnField.name == 'item' ||
        listColumnField.name == 'description' ||
        listColumnField.name == 'expireAt' ||
        listColumnField.name == 'createdAt' ||
        listColumnField.name == 'lastUpdated')
    ) {
      MessageUtil.warning({
        summary: LanguageUtil.get({ id: 'messages.failed.requiredFailed', defaultMessage: 'Required Failed' }),
        detail: LanguageUtil.get({ id: 'messages.warning.requiredCannotDeleted', defaultMessage: 'Required fields cannot be deleted!' }),
      });
      return;
    }

    list.fields = list.fields.filter((field) => field != listColumnField);
    changeList(list);
  };

  private onChangeType = (e: FormicaSelectBoxItem, record: ListColumnField) => {
    const {
      changeList,
      list: { list },
    } = this.props;
    record.type = e.value as DataType;
    changeList(list);
  };

  private onChangeFieldName = (e: ChangeEvent<HTMLInputElement>, record: ListColumnField) => {
    const {
      changeList,
      list: { list },
    } = this.props;
    record.name = (e.target as HTMLInputElement).value;
    changeList(list);
  };

  private onCheckKey = (record: ListColumnField) => {
    const {
      list: { list },
      changeList,
    } = this.props;
    list.fields.forEach((f) => (f.key = false));
    record.key = true;
    changeList(list);
  };

  private onChangeDescription = (e: ChangeEvent<HTMLInputElement>, record: ListColumnField) => {
    const {
      changeList,
      list: { list },
    } = this.props;
    record.description = (e.target as HTMLInputElement).value;
    changeList(list);
  };

  private getColumns = (): ColumnProps[] => {
    const {
      type,
      isUpdate,
      list: { dependencies },
    } = this.props;
    const isDisabled = this.isListDeployed() || !RoleHelper.canEdit('List');
    const columns = [
      {
        field: 'name',
        header: LanguageUtil.get({ id: 'pages.list.internal.table.name', defaultMessage: 'Name' }),
        className: 'w-1/5',
        body: (record: ListColumnField) => {
          const isUsed = dependencies.some((s) => s.dependentId == record.name);
          return (
            <CustomInput
              value={record.name}
              onChange={(e: ChangeEvent<HTMLInputElement>) => this.onChangeFieldName(e, record)}
              inputType={'ghost'}
              disabled={
                isDisabled ||
                isUpdate ||
                isUsed ||
                (type == 'INTERNAL_LIST' &&
                  (record.name == 'item' ||
                    record.name == 'description' ||
                    record.name == 'expireAt' ||
                    record.name == 'createdAt' ||
                    record.name == 'lastUpdated'))
              }
              type={'text'}
            />
          );
        },
      },
      {
        field: 'type',
        header: LanguageUtil.get({ id: 'pages.list.internal.table.type', defaultMessage: 'Type' }),
        className: 'w-1/5',
        body: (record: ListColumnField) => {
          const isUsed = dependencies.some((s) => s.dependentId == record.name);
          return (
            <div className={'flex items-center justify-start'}>
              <DataTypeSelectBoxComponent
                ghost
                className={'!border-none hover:!border-none !bg-transparent focus:!outline-none hover:!outline-none'}
                onChangeValue={(e: FormicaSelectBoxItem) => this.onChangeType(e, record)}
                disabled={
                  isDisabled ||
                  isUpdate ||
                  isUsed ||
                  (type == 'INTERNAL_LIST' &&
                    (record.name == 'item' ||
                      record.name == 'description' ||
                      record.name == 'expireAt' ||
                      record.name == 'createdAt' ||
                      record.name == 'lastUpdated'))
                }
                properties={{ dataType: record.type }}
                structEnabled={false}
                dateEnabled={false}
                listEnabled={false}
              />
            </div>
          );
        },
      },
      {
        field: 'description',
        header: LanguageUtil.get({ id: 'pages.list.internal.table.description', defaultMessage: 'Description' }),
        className: 'w-1/5',
        body: (record: ListColumnField) => {
          return (
            <CustomInput
              value={record.description}
              onChange={(e: ChangeEvent<HTMLInputElement>) => this.onChangeDescription(e, record)}
              inputType={'ghost'}
              type={'text'}
              disabled={isDisabled || isUpdate}
            />
          );
        },
      },
      {
        field: 'action',
        header: LanguageUtil.get({ id: 'pages.list.internal.table.action', defaultMessage: 'Action' }),
        className: 'max-w-[75px] 2xl:max-w-[100px]',
        sortable: false,
        body: (record: ListColumnField) => (
          <i
            className={`pi pi-trash w-full h-full flex items-center justify-center hover:text-primary 
            ${isDisabled || !this.canDelete(record.name) ? 'cursor-not-allowed' : 'cursor-pointer'} `}
            onClick={(e: any) =>
              !isDisabled &&
              this.canDelete(record.name) &&
              customConfirm({
                target: e.currentTarget,
                message: LanguageUtil.get({ id: 'messages.confirm.removeVariable', defaultMessage: 'Are you sure remove this variable?' }),
                accept: () => {
                  this.removeVariable(record);
                },
              })
            }
          />
        ),
      },
    ];
    if (type == 'EXTERNAL_LIST') {
      columns.splice(2, 0, {
        field: 'key',
        header: LanguageUtil.get({ id: 'pages.list.internal.table.key', defaultMessage: 'Key' }),
        className: 'w-1/5',
        body: (record: ListColumnField) => {
          if (record.type != DataType.STRING) return <></>;
          return (
            <div className={'text-left text-xs 2xl:text-md'}>
              <RadioButton
                checked={record.key}
                disabled={isDisabled || isUpdate}
                data-testid={`${ComponentName.LIST}_key_${record.name}`}
                onChange={() => this.onCheckKey(record)}
              />
            </div>
          );
        },
      });
    }
    return columns;
  };

  private canDelete = (fieldName: string) => {
    const {
      list: { list },
    } = this.props;
    const field = list.fields.find((f) => f.name == fieldName);
    if (field == undefined) return false;
    if (this.isListDeployed()) return false;
    return !(
      list.type == 'INTERNAL_LIST' &&
      (field.name == 'item' || field.name == 'description' || field.name == 'expireAt' || field.name == 'createdAt' || field.name == 'lastUpdated')
    );
  };

  private isRowSelectable = (e: DataTableDataSelectableParams) => {
    return !this.excluded.includes(e.data.name);
  };
}

interface Props extends ReduxProps {
  type: ListType;
}

const mapStateToProps = (state: RootState) => ({
  list: state.list as ListState,
  component: state.component as ComponentState,
  isUpdate: state.list.isUpdate,
});
const mapDispatchToProps = { changeList, changeListIsUpdate, changeListData };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(ListVariableComponent);
